import React, { useCallback, useEffect, useState } from "react"
import AddressSelectionMap from "./AddressSelectionMap"
import { Address, Coordinates, Place, SavedAddress } from "../../types/address"
import SearchIcon from "../icons/SearchIcon"
import LocateIcon from "../icons/LocateIcon"
import LocationIcon from "../icons/LocationIcon"
import Skeleton from "react-loading-skeleton"
import SearchSection from "./SearchSection"
import { fetchPlace, fetchDecodedAddress, getReverseGeocode } from "../../services/api/address"
import CompleteAddress from "./CompleteAddress"
import AddressList from "./AddressList"
import { useDispatch } from "react-redux"
import { fetchAddressesThunk } from "../../redux-stores/slices/addressSlice"
import { AppDispatch } from "../../redux-stores/store"
import { APIProvider } from "@vis.gl/react-google-maps"
import { GOOGLE_MAPS_API_KEY, GOOGLE_MAPS_API_KEY_APP } from "../../constants/constants"
import { set } from "lodash"
import { useLocationPermission } from "../../hooks/useLocationPermission"
import { useLocationAccess } from "../../contexts/LocationContext"
import { isNativePlatform } from "../../utils/platformUtil"

interface AddressBookProps {
    onSelection?: (address: SavedAddress) => void
    toggleAddNewSection?: (add: boolean) => void
}
const indiaCenter = {
    latitude: 22.9734, // Latitude
    longitude: 78.6569  // Longitude
};

const AddressBook: React.FC<AddressBookProps> = ({ onSelection, toggleAddNewSection }) => {

    const [addNew, setAddNew] = useState(false);
    const [coordinates, setCoordinates] = useState<Coordinates | null>(null);

    // const [place, setPlace] = useState<Place | null>(null);
    const [decodedAddress, setDecodedAddress] = useState<Address | null>(null);
    const [selectedPlace, setSelectedPlace] = useState<any>(null);
    const [selectedAddressUUID, setSelectedAddressUUID] = useState<string | null>(null);

    const [fetching, setFetching] = useState(true);

    const [searchSection, setSearchSection] = useState(false);

    const [confirmLocation, setConfirmLocation] = useState(false);

    const dispatch: AppDispatch = useDispatch();

    const {location, requestLocation, error: locationError, declined} = useLocationAccess();


    const fetchAddressBook = useCallback((force = false) => {
        dispatch(fetchAddressesThunk({ force }));
    }, [dispatch])

    const reverseGeocoding = async (coords: Coordinates) => {
        try {
            setFetching(true);
            const result = await getReverseGeocode(coords);
            if (Array.isArray(result) && result.length > 0) {
                const reverseGeoCodedPlace: Place = result[0];
                const decoded = await fetchDecodedAddress(reverseGeoCodedPlace.place_id);
                setDecodedAddress(decoded);
                // setPlace(reverseGeoCodedPlace);
            }
        } catch (error) {
            console.log(error);
        } finally {
            setFetching(false);
        }
    }

    const fetchPlaceData = async (placeId: string) => {
        try {
            setFetching(true);
            const result: Address = await fetchDecodedAddress(placeId);
            if (result) {
                setCoordinates({ latitude: result.lat, longitude: result.lng });
                setDecodedAddress(result);
            }
        } catch (error) {
            console.log(error);
        } finally{
            setFetching(false);
        }
    }

    useEffect(() => {
        if(location){
            setCoordinates(location);
            reverseGeocoding(location);
        }
    }, [location])

    useEffect(() => {
        if (addNew && requestLocation) {
            requestLocation();
        }
    }, [addNew, requestLocation])

    useEffect(() => {
        if( toggleAddNewSection ) {
            toggleAddNewSection(addNew);
        }
    }, [addNew, toggleAddNewSection])

    // useEffect(() => {
    //     if (coordinates) {
    //         reverseGeocoding(coordinates)
    //     }
    // }, [coordinates])

    useEffect(() => {
        if(locationError && !location){
            console.log(locationError);
            setCoordinates(indiaCenter)
        }else if(location){
            setCoordinates(location);
        }
    }, [location, locationError])

    return (
        <div className="address-selection">
            {
                addNew ? (
                    searchSection ? (
                        <SearchSection
                            onCancel={() => {
                                setSearchSection(false)
                                requestLocation();
                            }}
                            currentGeolocation={location ?? indiaCenter}
                            onPlaceSelection={(place) => {
                                console.log('Selected Place:', place);
                                setSearchSection(false);
                                fetchPlaceData(place.placePrediction.placeId)
                                setSelectedPlace(place);
                                // setPlace(place);
                            }}
                        />
                    ) :
                        (
                            <div className="map position-relative">
                                <div className="search-mockup-wrapper">
                                    <div className="search-mock " onClick={() => setSearchSection(true)}>
                                        <span className="search-text">Search a building, street name or area</span>
                                        <SearchIcon />
                                    </div>
                                </div>
                                <APIProvider apiKey={isNativePlatform() ? GOOGLE_MAPS_API_KEY_APP : GOOGLE_MAPS_API_KEY}>
                                    <AddressSelectionMap
                                        coordinates={coordinates ?? { latitude: 0, longitude: 0 }}
                                        setCoordinates={(coords) => {
                                            setCoordinates(coords);
                                            reverseGeocoding(coords);
                                        }}
                                    />
                                </APIProvider>
                                <div className="located-address-wrapper">
                                    {
                                        confirmLocation && coordinates && decodedAddress ? (
                                            <CompleteAddress
                                                decodedAddress={decodedAddress}
                                                coords={coordinates}
                                                onCancel={() => setConfirmLocation(false)}
                                                afterSubmit={(uuid) => {
                                                    setAddNew(false)
                                                    fetchAddressBook(true);
                                                    setSelectedAddressUUID(uuid);
                                                }}
                                            />
                                        ) :
                                            (
                                                <React.Fragment>
                                                    <div className="locate-me cursor-pointer mb-3" onClick={e => requestLocation()}>
                                                        <LocateIcon />
                                                        <span>Use Current Location</span>
                                                    </div>
                                                    {
                                                        (location || decodedAddress) &&
                                                        <div className="located-address">
                                                            <div className="located-address-content p-0">
                                                                <div className="located-address-content-secondary d-flex justify-content-between align-items-center gap-2">
                                                                    {
                                                                        fetching ? <Skeleton width={150} /> : <span>{decodedAddress?.formatted}</span>
                                                                    }
                                                                    <button onClick={() => setSearchSection(true)} className="change-location-btn">Change</button>
                                                                </div>
                                                            </div>
                                                            <div className="confirm-location-wrapper">
                                                                <button onClick={() => setConfirmLocation(true)} className="btn-primary btn w-100">
                                                                    Confirm Location
                                                                </button>
                                                            </div>
                                                        </div>
                                                    }
                                                </React.Fragment>
                                            )
                                    }

                                </div>
                            </div>
                        )
                ) :
                    (
                        <AddressList
                            onAddNew={() => {
                                setAddNew(true)
                            }}
                            onSelection={onSelection}
                            selectedAddressUUID={selectedAddressUUID}
                        />
                    )
            }
        </div>
    )
}

export default AddressBook